import React, { useEffect, useState } from "react"
import { FormDatasSubmission } from "../../../api/FormData/FormDataSubmissionApi"
import { useLocation, useNavigate } from "react-router-dom"
import {
    Button,
    DialogContent,
    Stack,
    TextField,
    Snackbar,
    Alert,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    SelectChangeEvent,
    IconButton,
    Grid,
    Dialog,
    DialogActions,
    Typography,
    Autocomplete,
} from "@mui/material"
import Header from "../../Layouts/AppHeader/AppHeader"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons"
import { EmployeeListArray } from "../../../@types/DRO/FormSubmissionResponse"

interface VisitorFormData {
    id: string
    visitorName: string
    visitDate: string
    contactNumber: string
    govtIdType: string
    govtIdNumber: string
    photoId: string
    entryTime: string
    exitTime: string
    address: string
    toMeet: string
    purpose: string
    status: string
}
const VisitorFormPage: React.FC = () => {
    const getCurrentDate = () => {
        const now = new Date()
        const day = String(now.getDate()).padStart(2, "0")
        const month = String(now.getMonth() + 1).padStart(2, "0")
        const year = now.getFullYear()
        return `${year}-${month}-${day}`
    }

    const getCurrentTime = () => {
        const now = new Date()
        const hours = String(now.getHours()).padStart(2, "0")
        const minutes = String(now.getMinutes()).padStart(2, "0")
        return `${hours}:${minutes}`
    }

    const [formData, setFormData] = useState<VisitorFormData>({
        id: "",
        visitorName: "",
        visitDate: getCurrentDate(),
        contactNumber: "",
        govtIdType: "",
        govtIdNumber: "",
        photoId: "",
        entryTime: getCurrentTime(),
        exitTime: "",
        address: "",
        toMeet: "",
        purpose: "",
        status: "ENTERED",
    })
    const [successDialogOpen, setSuccessDialogOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState({
        contactNumber: "",
        address: "",
        govtIdNumber: "",
    })
    const [employeeList, setEmployeeList] = useState<EmployeeListArray[]>([])
    const [searchQuery, setSearchQuery] = useState("")
    const location = useLocation()
    const navigate = useNavigate()

    const handleChange = (
        e:
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | SelectChangeEvent<string>
    ) => {
        const { name, value } = e.target
        let error = ""

        if (name === "contactNumber") {
            const isValid = /^\d*$/.test(value) && value.length <= 10
            if (!isValid) return
            if (value.length < 10) {
                error = "Contact number must be at least 10 digits."
            }
        }

        if (name === "address" && value.length < 5) {
            error = "Address must be at least 5 characters."
        }

        if (name === "govtIdNumber" && value.length < 5) {
            error = "Government ID must be at least 5 characters."
        }

        setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [name]: error,
        }))

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }))
    }

    useEffect(() => {
        if (location.state) {
            const { mobileNumber, fileId, visitorName, govtIdType, govtIdNumber, address } =
                location.state as {
                    mobileNumber: string
                    fileId: string
                    visitorName: string
                    govtIdType: string
                    govtIdNumber: string
                    address: string
                }
            setFormData((prevData) => ({
                ...prevData,
                contactNumber: mobileNumber || prevData.contactNumber,
                photoId: fileId || prevData.photoId,
                visitorName: visitorName || prevData.visitorName,
                govtIdType: govtIdType || prevData.govtIdType,
                govtIdNumber: govtIdNumber || prevData.govtIdNumber,
                address: address || prevData.address,
            }))
        }
    }, [location.state])

    useEffect(() => {
        if (successDialogOpen) {
            const timer = setTimeout(() => {
                setSuccessDialogOpen(false)
                navigate("/home")
            }, 3000)

            return () => clearTimeout(timer)
        }
    }, [successDialogOpen, navigate])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.photoId) {
            setErrorMessage("File ID is required.")
            return
        }

        const updatedFormData = {
            ...formData,
            status: "ENTERED",
        }

        try {
            const response = await FormDatasSubmission.api.insertVisitorInfo(
                updatedFormData
            )
            console.log("Visitor info inserted successfully:", response)

            setFormData({
                id: "",
                visitorName: "",
                visitDate: getCurrentDate(),
                contactNumber: "",
                govtIdType: "",
                govtIdNumber: "",
                photoId: "",
                entryTime: getCurrentTime(),
                exitTime: "",
                address: "",
                toMeet: "",
                purpose: "",
                status: "ENTERED",
            })

            setSuccessMessage("Visitor info inserted successfully!")
            setSuccessDialogOpen(true) // Open the success dialog
        } catch (error: any) {
            console.error("Failed to insert visitor info", error)
            const errorMessage =
                error.response?.data.message ||
                "Failed to insert visitor info. Please try again."
            setErrorMessage(errorMessage)
        }
    }

    const isFormValid = () => {
        return (
            formData.visitorName &&
            formData.visitDate &&
            formData.contactNumber &&
            formData.govtIdType &&
            formData.govtIdNumber &&
            formData.entryTime &&
            formData.address &&
            formData.toMeet &&
            formData.purpose &&
            !fieldErrors.contactNumber &&
            !fieldErrors.address &&
            !fieldErrors.govtIdNumber
        )
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setFormData((prevData) => ({
                ...prevData,
                entryTime: getCurrentTime(),
            }))
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const handleBackClick = () => {
        navigate(-1)
    }

    const fetchEmployeeList = async (search: string) => {
        try {
            const response = await FormDatasSubmission.api.getEmployeeList(
                "0",
                "10",
                search
            )
            setEmployeeList(response.data)
        } catch (error) {
            console.error("Failed to fetch employee list", error)
        }
    }

    useEffect(() => {
        fetchEmployeeList("")
    }, [])

    useEffect(() => {
        if (searchQuery !== null && searchQuery !== undefined) {
            fetchEmployeeList(searchQuery)
        }
    }, [searchQuery])

    return (
        <>
            <Header />
            <div
                className="mt-8 "
                style={{ marginTop: "60px", height: "60vh" }}
            >
                <div className="flex flex-row items-center justify-center gap-4 px-12 box-content">
                    <IconButton
                        onClick={handleBackClick}
                        sx={{ width: 10, aspectRatio: 1 / 1 }}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </IconButton>
                    <Typography variant="h4" gutterBottom sx={{ flexGrow: 1 }}>
                        Visitor's Form
                    </Typography>
                </div>

                <form
                    onSubmit={handleSubmit}
                    style={{
                        maxWidth: "600px",
                        margin: "0 auto",
                        padding: "0 16px",
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                fullWidth
                                label="Date"
                                name="visitDate"
                                type="date"
                                value={formData.visitDate}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="visitorName"
                                value={formData.visitorName}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                fullWidth
                                label="Contact Number"
                                name="contactNumber"
                                type="tel"
                                value={formData.contactNumber}
                                onChange={handleChange}
                                required
                                error={!!fieldErrors.contactNumber}
                                helperText={fieldErrors.contactNumber}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                required
                                error={!!fieldErrors.address}
                                helperText={fieldErrors.address}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Autocomplete
                                fullWidth
                                options={employeeList}
                                getOptionLabel={(option) => option.empName}
                                onInputChange={(event, newInputValue) => {
                                    setSearchQuery(newInputValue || "")
                                }}
                                onChange={(event, newValue) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        toMeet: newValue
                                            ? newValue.empName
                                            : "",
                                    }))
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="To Meet"
                                        required
                                    />
                                )}
                                value={
                                    employeeList.find(
                                        (emp) =>
                                            emp.empName === formData.toMeet
                                    ) || null
                                }
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="purpose-label">Purpose</InputLabel>
                                <Select
                                    labelId="purpose-label"
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={handleChange}
                                    label="Purpose"
                                    required
                                >
                                    <MenuItem value="Meeting">Meeting</MenuItem>
                                    <MenuItem value="Interview">Interview</MenuItem>
                                    <MenuItem value="Raise complaints">Raise complaints</MenuItem>
                                    
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={6} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="govt-id-type-label">
                                    Government ID Type
                                </InputLabel>
                                <Select
                                    labelId="govt-id-type-label"
                                    id="govt-id-type-select"
                                    name="govtIdType"
                                    value={formData.govtIdType}
                                    onChange={handleChange}
                                    label="Government ID Type"
                                    required
                                >
                                    <MenuItem value="Aadhar">
                                        Aadhar Number
                                    </MenuItem>
                                    <MenuItem value="Driving License">
                                        Driving License
                                    </MenuItem>
                                    <MenuItem value="Passport">
                                        Passport Number
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            {formData.govtIdType && (
                                <TextField
                                    fullWidth
                                    label={formData.govtIdType}
                                    name="govtIdNumber"
                                    value={formData.govtIdNumber}
                                    onChange={handleChange}
                                    required
                                    error={!!fieldErrors.govtIdNumber}
                                    helperText={fieldErrors.govtIdNumber}
                                />
                            )}
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                fullWidth
                                label="In Time"
                                name="entryTime"
                                type="time"
                                value={formData.entryTime}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required
                                disabled
                            />
                        </Grid>
                    </Grid>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                            backgroundColor: "#1FCD5B",
                            "&:hover": {
                                backgroundColor: "#17B34A",
                            },
                            marginTop: 2,
                        }}
                        disabled={!isFormValid()}
                    >
                        Submit
                    </Button>
                </form>
                <Dialog
                    open={successDialogOpen}
                    onClose={() => setSuccessDialogOpen(false)}
                    PaperProps={{
                        style: {
                            borderRadius: 15,
                        },
                    }}
                >
                    <DialogContent>
                        <Typography
                            style={{
                                color: "green",
                                textAlign: "center",
                                fontSize: "1.2rem",
                                marginTop: 10,
                            }}
                        >
                            {successMessage}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setSuccessDialogOpen(false)
                                navigate("/home")
                            }}
                            color="warning"
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                {errorMessage && (
                    <Snackbar
                        open={!!errorMessage}
                        autoHideDuration={6000}
                        onClose={() => setErrorMessage(null)}
                    >
                        <Alert
                            onClose={() => setErrorMessage(null)}
                            severity="error"
                        >
                            {errorMessage}
                        </Alert>
                    </Snackbar>
                )}
            </div>
        </>
    )
}

export default VisitorFormPage